import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { ProductionRequestService } from "../../services/production-request.service";
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component";
import { PermissionService } from "../../services/permission.service";
import { PageHeaderComponent } from "../../shared/page-header/page-header.component";
import { OwnerStationFiltersComponent } from "../../shared/owner-station-filters/owner-station-filters.component";
import { OwnerStationFilterValues } from "../../models/filter.model";
import {
  ProductionRequest,
  PRODUCTION_REQUEST_STATUS_LABELS,
  PRODUCTION_REQUEST_STATUS_CLASS,
  ProductionRequestStatus,
} from "../../models/production-request.model";

@Component({
  selector: "app-production-request-list",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    PageHeaderComponent,
    DataLoaderComponent,
    OwnerStationFiltersComponent,
  ],
  templateUrl: "./production-request-list.component.html",
  styleUrls: ["./production-request-list.component.css"],
})
export class ProductionRequestListComponent implements OnInit {
  requests: ProductionRequest[] = [];
  loading = false;
  error = "";
  statusLabels = PRODUCTION_REQUEST_STATUS_LABELS;
  statusClass = PRODUCTION_REQUEST_STATUS_CLASS;
  isUserRole = false;
  userRole = "";
  showOwnerStationFilters = false;

  ownerFilterId: number | null = null;
  stationFilterId: number | null = null;

  constructor(
    private productionRequestService: ProductionRequestService,
    private permissionService: PermissionService
  ) {}

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // The effective role changes when the user switches station, and this page
    // stays mounted while that happens, so the role-derived flags are re-applied
    // on every emission instead of being read once. currentRole$ is a
    // BehaviorSubject, so the first emission is synchronous.
    this.permissionService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.userRole = this.permissionService.getCurrentRole();
        this.isUserRole = this.userRole === "user";
        this.showOwnerStationFilters = !this.isUserRole;
      });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = "";
    const params: { owner_id?: number; station_id?: number } = {};
    if (this.ownerFilterId != null) params.owner_id = this.ownerFilterId;
    if (this.stationFilterId != null) params.station_id = this.stationFilterId;

    this.productionRequestService.list(params).subscribe({
      next: (data) => {
        this.requests = Array.isArray(data) ? data : [];
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.error || "Something went wrong. Please try again.";
        this.loading = false;
      },
    });
  }

  onOwnerStationFilterChange(values: OwnerStationFilterValues): void {
    this.ownerFilterId = values.ownerId;
    this.stationFilterId = values.stationId;
    this.load();
  }

  getStatusClass(status: ProductionRequestStatus): string {
    return this.statusClass[status] || "status-pending";
  }
}
